import type { AdminBillingViewDTO, BillingConfirmInput, BillingTariffChangePreviewDTO, TariffCatalogDTO } from "@ai-crm/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { ConfirmDialog } from "../../components/confirm-dialog";
import { adminApi } from "../../lib/api";
import { formatMoscowDateTime } from "../../lib/format";
import { paymentButton, paymentPrimary, paymentError, paymentKeys, usePaymentScope, usePaymentWrite, usePreviewExpiry } from "../../lib/payment-ui";

interface BillingTariffChangePanelProps {
  accountId: string;
  billing: AdminBillingViewDTO;
  catalog: TariffCatalogDTO;
}
interface ChangeIntent { preview: BillingTariffChangePreviewDTO; body: BillingConfirmInput }

export function BillingTariffChangePanel({ accountId, billing, catalog }: BillingTariffChangePanelProps) {
  const scope = usePaymentScope(accountId);
  const write = usePaymentWrite(accountId);
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tariffId, setTariffId] = useState(billing.subscription.kind === "PAID" ? billing.tariff.id : "freemium");
  const [intent, setIntent] = useState<ChangeIntent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const previewPending = useRef(false);
  const expired = usePreviewExpiry(intent?.preview.expiresAt);
  const locked = loading || write.locked;
  const selectable = tariffId === "freemium" || (billing.subscription.kind === "PAID" && catalog.definition.paidTariffs.some((tariff) => tariff.id === tariffId));

  const refresh = () => Promise.allSettled([
    client.invalidateQueries({ queryKey: ["admin", "account", accountId] }),
    client.invalidateQueries({ queryKey: ["admin", "accounts"] }),
    client.invalidateQueries({ queryKey: paymentKeys.account(scope.adminId, accountId) }),
    client.invalidateQueries({ queryKey: ["admin", "tariffs"] }),
  ]);

  async function preview() {
    if (!scope.token || previewPending.current || write.locked) return;
    if (!selectable) { setError("Выберите тариф из текущего каталога."); return; }
    previewPending.current = true;
    const guard = scope.capture();
    setLoading(true); setError(null); write.clearError();
    try {
      const result = await adminApi.previewBillingTariffChange(scope.token, accountId, {
        tariffId, expectedBillingVersion: billing.billingVersion, expectedCatalogVersion: catalog.version,
      });
      if (!guard.isCurrent()) return;
      setIntent({ preview: result, body: { requestId: crypto.randomUUID(), previewToken: result.previewToken, expectedBillingVersion: result.billingVersion } });
    } catch (caught) {
      if (guard.isCurrent()) { setError(paymentError(caught)); await refresh(); }
    } finally {
      if (guard.isCurrent()) { setLoading(false); previewPending.current = false; }
    }
  }

  async function confirm() {
    if (!intent || !scope.token || expired) return;
    const token = scope.token;
    const guard = scope.capture();
    await write.run(intent.body, async (body) => {
      const result = await adminApi.confirmBillingTariffChange(token, accountId, body);
      if (!guard.isCurrent()) return;
      client.setQueryData(["admin", "account", accountId, "billing"], result.billing);
      setSuccess(`Тариф изменён: ${result.billing.tariff.name}.`);
    }, async () => {
      setIntent(null); setOpen(false); await refresh();
    }, async () => { await refresh(); });
  }

  const summary = intent?.preview;
  const description = summary ? [
    `${summary.from.name} → ${summary.to.name}. Изменение вступит в силу сразу после подтверждения.`,
    summary.resetsPeriod
      ? `Текущая подписка завершится. Будущие платные продления (${summary.futureSubscriptions}) будут отменены. Остаток платных и докупленных диалогов не переносится. Начнётся новый период: ${summary.monthlyDialogs} диалогов на 30 дней, расход с нуля.`
      : `Тариф действует до ${formatMoscowDateTime(summary.paidThroughAt!)} МСК, включая оплаченные продления. Лимит — ${summary.monthlyDialogs} диалогов за 30 дней. Границы периода, расход и докупки сохраняются.`,
    summary.invoicesToReview > 0 ? `Неприменённые счета (${summary.invoicesToReview}) потребуют ручной сверки перед активацией.` : "",
    "История сохраняется. Доплата и возврат денег не оформляются.",
    expired ? "Подтверждение устарело. Отмените его и получите новое." : "",
    write.error ?? "",
  ].filter(Boolean).join(" ") : "";

  return <section className="panel space-y-3 p-4" aria-label="Изменение тарифа">
    <button className={paymentButton} type="button" disabled={locked || billing.subscription.kind === "FREEMIUM"}
      aria-expanded={open} onClick={() => { setOpen(!open); setSuccess(null); }}>
      Изменить тариф
    </button>
    {billing.subscription.kind === "FREEMIUM" && <p className="text-sm text-muted-foreground">Для перехода на платный тариф используйте назначение подписки ниже.</p>}
    {success && <p role="status">{success}</p>}
    {open && <div className="space-y-3">
      <label className="block space-y-1 text-sm"><span>Новый тариф</span>
        <select className="control" value={tariffId} disabled={locked} onChange={(event) => { setTariffId(event.target.value); setIntent(null); setError(null); write.clearError(); }}>
          {!selectable && <option value={tariffId} disabled>{tariffId} — недоступен в текущем каталоге</option>}
          <option value="freemium">{catalog.definition.freemium.name}</option>
          {billing.subscription.kind === "PAID" && catalog.definition.paidTariffs.map((tariff) => <option key={tariff.id} value={tariff.id}>{tariff.name}</option>)}
        </select>
      </label>
      <p className="text-sm text-muted-foreground">{tariffId === "freemium"
        ? "Переход на Freemium завершает платный или пробный доступ и начинает новый период с нулевым расходом."
        : "Платный тариф изменится до конца уже оплаченного срока. Расход и докупки сохранятся."}</p>
      <button className={paymentPrimary} type="button" disabled={locked} onClick={() => void preview()}>Продолжить</button>
    </div>}
    {(error || write.error) && <p role="alert">{error ?? write.error}</p>}
    {write.uncertain && <button className={paymentButton} type="button" disabled={write.pending} onClick={() => void write.retry()}>Повторить тот же запрос</button>}
    {intent && !write.uncertain && !write.blocked && <ConfirmDialog
      title="Подтвердить изменение тарифа?" description={description} destructive={intent.preview.resetsPeriod}
      confirmLabel="Подтвердить изменение" confirmDisabled={expired || write.error !== null}
      isPending={write.pending} onConfirm={() => void confirm()} onCancel={() => { if (!write.locked) setIntent(null); }} />}
  </section>;
}
